import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { login, register, logout, updateUser } from './authAPI';

// Load initial state from localStorage
const loadInitialState = () => {
  const token = localStorage.getItem('accessToken');
  const userData = localStorage.getItem('userData');
  
  if (token && userData) {
    const { username, role, shopName } = JSON.parse(userData);
    return {
      username,
      shopName,
      token,
      role,
      loading: false,
      error: null,
    };
  }
  
  return {
    username: null,
    shopName: null,
    token: null,
    role: null,
    loading: false,
    error: null,
  };
};

const initialState = loadInitialState();

export const loginAsync = createAsyncThunk(
  'auth/login', 
  async (userData) => {
    const response = await login(userData);
    // Store user data and token in localStorage

    localStorage.setItem('accessToken', response.jwtToken);
    localStorage.setItem('refreshToken', response.refreshToken)

    localStorage.setItem('userData', JSON.stringify({
      username: response.username,
      role: response.role,
      shopName: response.shopName,
      approved: response.approved
    }));
    return response;
  }
);

export const registerAsync = createAsyncThunk(
  'auth/register', 
  async (userData) => {
    const response = await register(userData);
    return response;
  }
);

export const logoutAsync = createAsyncThunk(
  'auth/logout', 
  async () => {
    const response = await logout();
    // Clear localStorage on logout
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userData');
    return response;
  }
);

export const updateUserAsync = createAsyncThunk(
  'auth/updateProfileUser',
  async (userData) => {
    const response = await updateUser(userData);
    // Update user data in localStorage
    const currentUserData = JSON.parse(localStorage.getItem('userData') || '{}');
    localStorage.setItem('userData', JSON.stringify({
      ...currentUserData,
      ...response
    }));
    return response;
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser: (state, action) => {
      state.username = action.payload.username;
      state.token = action.payload.jwtToken;
      state.role = action.payload.role;
      state.shopName = action.payload.shopName;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload.error || action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(registerAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerAsync.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(registerAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(loginAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.username = action.payload.username;
        state.token = action.payload.jwtToken;
        state.role = action.payload.role;
        state.shopName = action.payload.shopName;
      })
      .addCase(loginAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(logoutAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(logoutAsync.fulfilled, (state) => {
        state.loading = false;
        state.username = null;
        state.token = null;
        state.role = null;
        state.shopName = null;
      })
      .addCase(logoutAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(updateUserAsync.pending, (state, action) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUserAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.username = action.payload.username;
        state.role = action.payload.role;
        state.shopName = action.payload.shopName;
      })
      .addCase(updateUserAsync.rejected, (state, action) => {
        state.loading = false;
      });
  },
});

export const selectRole = (state) => state.auth.role;
export const { setUser, setLoading, setError } = authSlice.actions;
export default authSlice.reducer; 